import createDOMPurify from 'dompurify';

import { isAllowedLinkHref } from './linkPolicy';

/**
 * URI schemes permitted in sanitized content links. Mirrors the editor and
 * backend whitelists (linkPolicy / ALLOWED_LINK_PROTOCOLS): http, https,
 * mailto, tel — plus relative URLs and fragments. javascript:, data: and other
 * schemes are rejected.
 */
// The runtime owns this purifier. The admin sanitizer adds its own hook to the
// package-default `DOMPurify`, and both sanitizers load in the same document, so a
// hook on the default instance would also run on published page content.
const contentPurifier = createDOMPurify(window);

// Force the shared link policy and a safe rel on links that open in a new tab.
contentPurifier.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName !== 'A') {
    return;
  }

  const href = node.getAttribute('href');

  if (href && !isAllowedLinkHref(href)) {
    node.removeAttribute('href');
  }

  if (node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/**
 * Sanitize an HTML string for rendering as rich-text content. Allows the link
 * mark's attributes while enforcing the protocol whitelist and a safe rel.
 */
export function sanitizeContentHtml(html: string): string {
  return contentPurifier.sanitize(html, {
    ADD_ATTR: ['target']
  });
}

/**
 * Tags a page wireframe may be drawn with. Mirrors the `wireframe` set in
 * config/purify.php, which is the pass that actually gates what gets stored.
 */
const WIREFRAME_TAGS = [
  'div',
  'section',
  'header',
  'footer',
  'nav',
  'aside',
  'hr',
  'br',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'span',
  'strong',
  'em',
  'small',
  'ul',
  'ol',
  'li',
  'dl',
  'dt',
  'dd',
  'figure',
  'figcaption',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'label',
  'input',
  'textarea',
  'select',
  'option',
  'button'
];

/**
 * Class values a wireframe may carry. Layout only, and mirrors `Attr.AllowedClasses`
 * in config/purify.php, which is the pass that gates what gets stored.
 */
const WIREFRAME_CLASSES = new Set(['row', 'cols-2', 'cols-3', 'cols-4', 'stack']);

/**
 * A purifier of its own, because a hook belongs to the instance it is added to and
 * not to the call that wanted it.
 *
 * Registered on the content purifier, the class filter below would also run for
 * `sanitizeContentHtml`, which is how rich text reaches a published page. A rule written for one review screen would have been
 * quietly deleting classes off customer content, and the link mark carries one
 * through its whole round trip (see `tipTapLink.ts`).
 */
const wireframePurifier = createDOMPurify(window);

// Drop any class the wireframe vocabulary does not name. Scoped to nodes carrying a
// class so it costs nothing on the rest of the drawing.
wireframePurifier.addHook('afterSanitizeAttributes', (node) => {
  if (!(node instanceof Element)) {
    return;
  }

  const classAttribute = node.getAttribute('class');

  if (classAttribute === null) {
    return;
  }

  const kept = classAttribute.split(/\s+/).filter((name) => WIREFRAME_CLASSES.has(name));

  if (kept.length > 0) {
    node.setAttribute('class', kept.join(' '));
  } else {
    node.removeAttribute('class');
  }
});

/**
 * Sanitize a planned page wireframe for rendering.
 *
 * The second of two passes. The Planner's markup is already cleaned where it is
 * stored, so in the ordinary case this changes nothing — which is the point: the
 * value reaching here came out of a database column that a model wrote into, and
 * one pass standing between that and the DOM is one pass too few.
 *
 * Structure and layout only. No `style` or `id` survives and `class` is reduced to
 * the five layout words above, so a wireframe cannot carry a color; no `href` or
 * `src`, so it cannot address anything. What is left is the shape of the region,
 * which is all a low-fidelity drawing is.
 */
export function sanitizeWireframeHtml(html: string): string {
  return wireframePurifier.sanitize(html, {
    ALLOWED_TAGS: WIREFRAME_TAGS,
    ALLOWED_ATTR: ['type', 'placeholder', 'colspan', 'rowspan', 'class']
  });
}
